import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { fmtNumber } from '../lib/localeUtils';

export const AIQuotaNudge = () => {
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/quota');
      setQuota(data);
    } catch {
      setQuota(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 300000);
    window.addEventListener('pharmxai-ai-usage-changed', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pharmxai-ai-usage-changed', refresh);
    };
  }, [refresh]);

  if (!quota?.warning_level) return null;

  const urgent = quota.warning_level >= 95;
  return (
    <div
      className={`mx-4 mt-4 lg:mx-6 rounded-md border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${
        urgent
          ? 'border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100'
          : 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
      }`}
      data-testid="ai-quota-upgrade-nudge"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="mt-0.5 shrink-0">
          {urgent ? <AlertTriangle className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
        </div>
        <div className="min-w-0" data-testid="ai-quota-upgrade-nudge-message">
          <p className="text-sm font-semibold">
            {urgent ? 'AI allowance nearly used' : 'You are approaching your AI allowance'}
          </p>
          <p className="text-sm opacity-80">
            {fmtNumber(quota.used)} of {fmtNumber(quota.limit)} calls used this month · {fmtNumber(quota.remaining)} remaining on {quota.tier_name}.
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant={urgent ? 'destructive' : 'outline'}
        className="shrink-0 gap-1.5"
        onClick={() => navigate('/subscription')}
        data-testid="ai-quota-upgrade-button"
      >
        Upgrade plan <ArrowUpRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};